'use client';

import { Flex, IconButton, Link, SpriteIcon, Typography } from '@coolblue-development/freddy';
import clickInformationIconTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/clickInformationIconTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import { Product as GlobalProduct, Product, Property } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import { Discriminant, Variant } from '../Collection.types';

type PropertyTitleProps = {
  discriminant: Discriminant;
  shouldShowCollectionTitle: boolean;
  variant: Variant;
  product: GlobalProduct;
};

export const PropertyTitle = ({
  discriminant,
  shouldShowCollectionTitle,
  variant,
  product
}: PropertyTitleProps) => {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop.productPage');
  const isMobile = variant === Variant.MOBILE;

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const getMatchingProperty = (product: Product, collectionPropertyId: string | null) => {
    if (!collectionPropertyId) {
      return undefined;
    }

    const properties =
      product.subProducts.length > 0
        ? (product.subProducts.find(
            subProduct => subProduct.product?.subProductTypeId === product?.subProductTypeId
          )?.product.properties ?? product.properties)
        : product.properties;

    return properties.propertyGroups
      .flatMap(propertyGroup => propertyGroup.properties)
      .find(property => property.propertyId === collectionPropertyId);
  };

  const property = getMatchingProperty(product, discriminant.referenceId);

  const onExplanationClick = () => {
    if (!property || !property.hasExplanation) {
      return;
    }

    trackEvent(
      clickInformationIconTrigger({
        sub_feature: 'multi_discriminant_selector',
        property_name: property.displayName
      })
    );

    setSelectedProperty(property);
  };

  return (
    <Flex alignItems='flex-end' {...(isMobile && { gap: 100 })}>
      <Typography mb={200} fontWeight={shouldShowCollectionTitle ? 600 : 700}>
        {discriminant.displayName}
      </Typography>
      {property &&
        property.hasExplanation &&
        (isMobile ? (
          <Link component='button' onClick={onExplanationClick} mb={200}>
            {t('collection.explanationLink')}
          </Link>
        ) : (
          <IconButton
            aria-label={t('specificationsInfo', {
              spec: discriminant.displayName
            })}
            sx={{
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
            onClick={onExplanationClick}
          >
            <SpriteIcon name='info-circled' color='base' />
          </IconButton>
        ))}
      {selectedProperty && product.productType && (
        <PropertyExplanationModal
          open={!!selectedProperty}
          onClose={() => setSelectedProperty(null)}
          property={selectedProperty}
          productTypeId={product.productType.productTypeId.toString()}
        />
      )}
    </Flex>
  );
};
